export const TEMPLATE_NAME_MAX = 512;
export const HEADER_MAX = 60;
export const BODY_MAX = 1024;
export const FOOTER_MAX = 60;

const VARIABLE_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;
const VARIABLE_CANDIDATE_PATTERN = /\{\{([^{}]+)\}\}/g;

export const normalizeTemplateName = (value = '') =>
	String(value)
		.toLowerCase()
		.trim()
		.replace(/\s+/g, '_')
		.replace(/[^a-z0-9_]/g, '');

export const extractPositionalVariables = (value = '') => {
	const indexes = [
		...String(value).matchAll(VARIABLE_PATTERN),
	].map((match) => Number(match[1]));

	return [...new Set(indexes)]
		.filter((number) => Number.isInteger(number) && number > 0)
		.sort((a, b) => a - b);
};

/**
 * Insert the next positional variable at `caret`, returning the new text and
 * where the caret should land.
 *
 * ★ PURE, BECAUSE THE BUG IT FIXES WAS INVISIBLE IN THE COMPONENT.
 * "Add variable" used to append to the end, which is precisely the shape
 * `validateVariablePlacement` rejects (Meta error 100) — so the button always
 * produced an invalid template. That was only discoverable by clicking it.
 * Here every case is assertable.
 *
 * A space is added before the token when the preceding text does not already
 * end in whitespace, so words never run together — and never otherwise, so no
 * spacing is invented that the writer did not ask for.
 *
 * @param {string} value  the current text
 * @param {number|null} caret  insertion point; end of the text when not a number
 * @returns {{value: string, caret: number}}
 */
export const insertVariableAt = (value = '', caret = null) => {
	const text = String(value);

	const at =
		Number.isInteger(caret) && caret >= 0 && caret <= text.length
			? caret
			: text.length;

	const indexes = extractPositionalVariables(text);
	const next = indexes.length ? Math.max(...indexes) + 1 : 1;

	const before = text.slice(0, at);
	const after = text.slice(at);
	const lead = before && !/\s$/.test(before) ? ' ' : '';
	const token = `{{${next}}}`;

	return {
		value: `${before}${lead}${token}${after}`,
		caret: before.length + lead.length + token.length,
	};
};

export const validatePositionalVariables = (value = '') => {
	const text = String(value);

	if (!text.includes('{{')) {
		return true;
	}

	const candidates = [
		...text.matchAll(VARIABLE_CANDIDATE_PATTERN),
	];

	if (!candidates.length) {
		return 'Variable syntax is invalid. Use {{1}}, {{2}}, {{3}}, etc.';
	}

	for (const match of candidates) {
		const valueInsideBraces = match[1]?.trim();

		if (!/^[1-9]\d*$/.test(valueInsideBraces)) {
			return 'Only positional variables such as {{1}}, {{2}} and {{3}} are supported.';
		}
	}

	const indexes = extractPositionalVariables(text);

	for (let i = 0; i < indexes.length; i += 1) {
		if (indexes[i] !== i + 1) {
			return `Variables must be sequential. Expected {{${i + 1}}}.`;
		}
	}

	return validateVariablePlacement(text);
};

/**
 * Meta's placement rule, applied BEFORE the request leaves the browser.
 *
 * ★ A COMPONENT'S TEXT MAY NOT BEGIN OR END WITH A VARIABLE. Meta refuses
 * such a template with code 100, "Leading or Trailing Params Not Allowed" —
 * true, and useless to the person who typed it. This screen made that outcome
 * the default: "Add variable" appends `{{n}}` to the end of the text, so
 * typing a sentence, adding a variable and pressing Create produced exactly
 * the shape Meta rejects, and the only feedback was Meta's phrase after a
 * round trip through two services. The check runs on every keystroke and on
 * the add-variable button (the form validates on `setValue`), so the message
 * appears the moment the variable lands, not after submit.
 *
 * Whitespace does not count as text: Meta trims before it checks, so
 * "{{1}} " is still a leading variable and " {{1}}" is still trailing.
 */
const LEADING_VARIABLE = /^\s*\{\{\s*\d+\s*\}\}/;
const TRAILING_VARIABLE = /\{\{\s*\d+\s*\}\}\s*$/;

export const validateVariablePlacement = (value = '') => {
	const text = String(value);
	if (!text.includes('{{')) return true;

	const trimmed = text.trim();
	const startsWithVariable = LEADING_VARIABLE.test(trimmed);
	const endsWithVariable = TRAILING_VARIABLE.test(trimmed);

	if (startsWithVariable && endsWithVariable) {
		return 'WhatsApp needs some words around variables — the text cannot start or end with one. Try "Hi {{1}}, your viewing is confirmed."';
	}
	if (startsWithVariable) {
		return 'WhatsApp does not allow text to start with a variable. Add a word or two before it, e.g. "Hi {{1}}".';
	}
	if (endsWithVariable) {
		return 'WhatsApp does not allow text to end with a variable. Add some words after it, e.g. "{{1}} is confirmed."';
	}
	return true;
};

/**
 * Meta allows a text header ONE variable at most.
 *
 * Distinct from the body rule so the header field can say why on its own;
 * a header that passes this still has to pass placement, so callers chain
 * both (see the form's header `validate`).
 */
export const validateHeaderVariables = (value = '') => {
	const indexes = extractPositionalVariables(value);
	if (indexes.length > 1) {
		return 'A header can contain at most one variable.';
	}
	return validatePositionalVariables(value);
};

const exampleValue = (examples, index) =>
	String(examples?.[index] ?? '').trim();

/** Meta's own ceilings for the button block. */
export const BUTTON_TEXT_MAX = 25;
export const MAX_QUICK_REPLIES = 10;
export const MAX_CALL_TO_ACTION = 2;

/** A URL button's variable, which Meta allows only as a SUFFIX. */
const URL_VARIABLE = /\{\{\s*\d+\s*\}\}/g;
const URL_VARIABLE_AT_END = /\{\{\s*\d+\s*\}\}\s*$/;

/**
 * Check a template's buttons against the rules Meta enforces on submit.
 *
 * ★ THE URL VARIABLE MUST BE A SUFFIX, and this is the button equivalent of the
 * body's placement rule. Meta builds the final link by APPENDING the parameter
 * to a fixed prefix, so `https://x.test/{{1}}/track` cannot be expressed — the
 * refusal arrives as a generic 100 that says nothing about which button, which
 * is a miserable thing to debug against a live API.
 *
 * ★ DUPLICATE LABELS ARE REFUSED because Meta refuses them, and the message it
 * returns does not mention that duplication is the problem.
 *
 * @returns {true|string} true, or the first problem in words
 */
export const validateButtons = (buttons = []) => {
	const list = Array.isArray(buttons) ? buttons.filter((b) => b && (b.text || b.url || b.phone)) : [];
	if (!list.length) return true;

	const quickReplies = list.filter((b) => b.type === 'QUICK_REPLY');
	const callsToAction = list.filter((b) => b.type === 'URL' || b.type === 'PHONE_NUMBER');

	if (quickReplies.length > MAX_QUICK_REPLIES) {
		return `A template can have at most ${MAX_QUICK_REPLIES} quick-reply buttons.`;
	}
	if (callsToAction.length > MAX_CALL_TO_ACTION) {
		return `A template can have at most ${MAX_CALL_TO_ACTION} link or call buttons.`;
	}
	if (list.filter((b) => b.type === 'PHONE_NUMBER').length > 1) {
		return 'Only one call button is allowed.';
	}

	const seen = new Set();
	for (const button of list) {
		const text = String(button.text ?? '').trim();
		if (!text) return 'Every button needs a label.';
		if (text.length > BUTTON_TEXT_MAX) {
			return `Button labels are at most ${BUTTON_TEXT_MAX} characters — “${text}” is ${text.length}.`;
		}
		const key = text.toLowerCase();
		if (seen.has(key)) return `Two buttons are both labelled “${text}”. Meta refuses duplicate labels.`;
		seen.add(key);

		if (button.type === 'URL') {
			const url = String(button.url ?? '').trim();
			if (!url) return `“${text}” needs a web address.`;
			if (!/^https?:\/\//i.test(url)) return `“${text}” needs a web address starting with https://`;

			const variables = url.match(URL_VARIABLE) ?? [];
			if (variables.length > 1) return `“${text}” can contain at most one variable.`;
			if (variables.length === 1 && !URL_VARIABLE_AT_END.test(url)) {
				return `In “${text}”, the variable has to be at the END of the address — Meta adds it to the end of a fixed link.`;
			}
		}

		if (button.type === 'PHONE_NUMBER') {
			const phone = String(button.phone ?? '').trim();
			if (!phone) return `“${text}” needs a phone number.`;
			if (!/^\+?[0-9]{6,20}$/.test(phone.replace(/[\s-]/g, ''))) {
				return `“${text}” needs a phone number in international format, e.g. +971500000000.`;
			}
		}
	}

	return true;
};

export const buildTemplateComponents = ({
	header,
	headerExamples,
	body,
	bodyExamples,
	footer,
	buttons,
}) => {
	const components = [];

	const cleanHeader = String(header ?? '').trim();
	const cleanBody = String(body ?? '').trim();
	const cleanFooter = String(footer ?? '').trim();

	if (cleanHeader) {
		const variables =
			extractPositionalVariables(cleanHeader);

		const component = {
			type: 'HEADER',
			format: 'TEXT',
			text: cleanHeader,
		};

		if (variables.length) {
			component.example = {
				header_text: variables.map((index) =>
					exampleValue(headerExamples, index)
				),
			};
		}

		components.push(component);
	}

	const bodyVariables =
		extractPositionalVariables(cleanBody);

	const bodyComponent = {
		type: 'BODY',
		text: cleanBody,
	};

	if (bodyVariables.length) {
		bodyComponent.example = {
			body_text: [
				bodyVariables.map((index) =>
					exampleValue(bodyExamples, index)
				),
			],
		};
	}

	components.push(bodyComponent);

	if (cleanFooter) {
		components.push({
			type: 'FOOTER',
			text: cleanFooter,
		});
	}

	// ★ THE FOOTER COMES FIRST, THEN THE BUTTONS. Meta reads the component list
	// in order and puts the buttons last in the bubble; emitting them before the
	// footer produces a template that is accepted and then renders with the
	// small print underneath the buttons.
	const usable = (Array.isArray(buttons) ? buttons : []).filter((b) => b && String(b.text ?? '').trim());
	if (usable.length) {
		components.push({
			type: 'BUTTONS',
			buttons: usable.map((button) => {
				const text = String(button.text).trim();
				if (button.type === 'URL') {
					const url = String(button.url ?? '').trim();
					const component = { type: 'URL', text, url };
					// A dynamic link needs a sample of the WHOLE address, not of the
					// parameter — Meta checks that the finished link resolves.
					if (URL_VARIABLE_AT_END.test(url)) {
						const sample = String(button.urlExample ?? '').trim();
						if (sample) component.example = [sample];
					}
					return component;
				}
				if (button.type === 'PHONE_NUMBER') {
					return { type: 'PHONE_NUMBER', text, phone_number: String(button.phone ?? '').trim() };
				}
				return { type: 'QUICK_REPLY', text };
			}),
		});
	}

	return components;
};

export const renderTemplatePreview = (
	value = '',
	examples = {}
) =>
	String(value).replace(
		VARIABLE_PATTERN,
		(match, index) => {
			const sample = examples?.[Number(index)];

			return sample?.trim()
				? sample
				: match;
		}
	);